import { AmPmLabel } from '@/components/DriverManagement/Manage/GridColumns/AmPmLabel'
import { ArrowRightOutlined } from '@ant-design/icons'
import { OrderProducts } from '@components/_SiteWide/OrderDisplay/OrderProducts'
import { OrderWindowTag, formatTimeWindow } from '@components/_SiteWide/OrderDisplay/OrderTimeFormats'
import { BBDTag, Horizontal, Texto } from '@gravitate-js/excalibrr'
import useDispatchWindowFilter from '@pages/OrderManagement/components/Grid/Filters/DispatchWindow/deprecated_useDispatchWindowFilter'
import useLoadingWindowFilter from '@pages/OrderManagement/components/Grid/Filters/LoadingWindow'
import useRunoutFilter from '@pages/OrderManagement/components/Grid/Filters/Runout/deprecated_useRunoutFilter'
import { ReadableDifference, ShowRunOutWarningClass, convertTimeSpanToReadable } from '@utils/general'
import { startCase } from 'lodash'
import { formatShiftDate } from '../../util'

export const scheduleColumnDefs = () => [
  {
    field: 'driver_name',
    headerName: 'Driver',
    rowGroup: true,
  },
  {
    field: 'shift_start',
    headerName: 'Shift',
    rowGroup: true,
    valueGetter: (params) => formatShiftDate(params?.data),
    cellRenderer: (params) => {
      return (
        <Horizontal gap={6}>
          <Texto>{params.value}</Texto>
          <AmPmLabel value={params.data?.shift_key?.includes('.am') ? 'am' : 'pm'} />
        </Horizontal>
      )
    },
  },
  {
    field: 'trip_index',
    maxWidth: 60,
    headerName: '',
    cellRenderer: (params) =>
      params.data.trip_index >= 0 ? (
        <BBDTag style={{ padding: 0 }} theme2>
          {params?.data.trip_index + 1}
        </BBDTag>
      ) : null,
  },
  {
    field: 'number',
    headerName: 'Number',
    valueGetter: (params) => params?.data?.trips?.[0]?.number,
  },
  {
    field: 'drops',
    headerName: 'Destination',
    valueGetter: (params) =>
      params?.data?.trips?.[0]?.drops?.map((drop) => `${drop.location_name}, ${drop.store_name}`),
    cellRenderer: (params) => (
      <>
        {params?.data?.trips?.[0]?.drops?.map((d, i) => (
          <div key={i}>
            {d.length > 1 ? <b>{d.length} More</b> : <b>{d?.location_name}</b>},<span className='ml-1'>{d.city}</span>
          </div>
        ))}
      </>
    ),
  },
  {
    field: 'loads',
    headerName: 'Terminal',
    valueGetter: (params) => params?.data?.trips?.[0]?.loads?.map((load) => load.location_name),
    cellRenderer: (params) => (
      <>
        {params?.data?.trips?.[0]?.loads?.map((d, i) => {
          const locationName = d.location_short_name || d.location_name
          const showArrow = i !== params?.data?.trips?.[0]?.loads.length - 1
          return (
            <div key={i}>
              {locationName}
              {showArrow && <ArrowRightOutlined style={{ color: 'var(--theme-color-2)' }} className='pl-1 pr-2' />}
            </div>
          )
        })}
      </>
    ),
  },
  {
    field: 'products',
    headerName: 'Products',
    minWidth: 200,
    valueGetter: (params) => params?.data?.trips?.[0]?.product_ratios?.map((product) => product.name),
    cellRenderer: (params) => {
      const order = params.data?.trips?.[0]
      return order ? <OrderProducts order={order} /> : null
    },
  },
  {
    field: 'trips?.[0]?.dispatch_window_start',
    headerName: 'Delivery Window',
    filter: useDispatchWindowFilter,
    filterParams: {
      accessor: 'trips',
      accessor2: '0',
    },
    valueGetter: (params) => params?.data?.trips?.[0]?.dispatch_window_start,
    cellRenderer: (params) => {
      if (params?.data?.trips?.[0]?.dispatch_window_start)
        return (
          <OrderWindowTag
            isLoadingWindow
            window={formatTimeWindow(
              params?.data?.trips?.[0]?.dispatch_window_start,
              params?.data?.trips?.[0]?.dispatch_window_end,
              params?.data?.trips?.[0]?.type?.toLowerCase() !== 'backhaul'
            )}
          />
        )
    },
  },
  {
    field: 'load_window_start',
    headerName: 'Load Window',
    cellRenderer: (params) => {
      if (params?.data?.trips?.[0]?.load_window_start)
        return (
          <OrderWindowTag
            window={formatTimeWindow(
              params?.data?.trips?.[0]?.load_window_start,
              params?.data?.trips?.[0]?.load_window_end,
              params?.data?.trips?.[0]?.type?.toLowerCase() !== 'backhaul'
            )}
          />
        )
    },
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    filter: useLoadingWindowFilter,
    filterParams: {
      accessor: 'trips',
      accessor2: '0',
    },
    valueGetter: (params) => params?.data?.trips?.[0]?.load_window_start,
  },
  {
    field: 'runout',
    headerName: 'Runout',
    filter: useRunoutFilter,
    filterParams: {
      accessor: 'trips',
      accessor2: '0',
      accessor3: 'dispatch_window_end',
    },
    valueGetter: (params) => {
      return params?.data?.trips?.[0]?.dispatch_window_end
    },
    cellRenderer: (params) => (
      <span className={ShowRunOutWarningClass(params?.data?.trips?.[0]?.dispatch_window_end)}>
        {ReadableDifference(
          params?.data?.trips?.[0]?.dispatch_window_end,
          undefined,
          true,
          params?.data?.trips?.[0]?.type?.toLowerCase() === 'backhaul'
        )}
      </span>
    ),
  },
  {
    field: 'market',
    headerName: 'Market',
    cellRenderer: (params) => params?.data?.trips?.[0]?.market,
    valueGetter: (params) => params?.data?.trips?.[0]?.market && startCase(params?.data?.trips?.[0]?.market),
  },
  {
    field: 'sector',
    headerName: 'Sector',
    valueGetter: (params) => params?.data?.trips?.[0]?.sector && startCase(params?.data?.trips?.[0]?.sector),
    cellRenderer: (params) => <Texto textTransform='capitalize'> {params?.data?.trips?.[0]?.sector}</Texto>,
  },
  {
    field: 'home_location',
    headerName: 'Depot',
    cellRenderer: (params) => params?.data?.home_location,
  },
  {
    headerName: 'Turn Time',
    field: 'turn_time',
    valueGetter: (params) =>
      params?.data?.trips?.[0]?.turn_time && convertTimeSpanToReadable(params?.data?.trips?.[0]?.turn_time),
  },
]
